import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Rule, Meta, Display, Icons, Chip } from '../components/Atoms';
import LanguageSwitcher from '../components/LanguageSwitcher';
import QuickPhotoModal from '../components/QuickPhotoModal';
import { useApi, useMultipleApi } from '../lib/api';
import { getTheme, toggleTheme } from '../lib/theme';
import { LoadingState, NeedsLoginState } from '../components/states';

/**
 * Pełnoekranowy hamburger menu (z extras.jsx).
 *
 * Z 04-component-composition.md: Menu jest przejściem do **Profilu hub**
 * (5 sub-routes: konto/ogród/profil pub/odznaki/pomoc) - tutaj pierwszy krok.
 *
 * Sekcje:
 *   1. Identity card - awatar (initials) + nazwa + sezon X
 *   2. Mini-stat strip - Zadania wykonane / Zbiory kg / Wpisy dziennika
 *   3. Lista menu z 3 grupami (rozdzielanymi liniami)
 *   4. Cytat na końcu (Fraunces italic)
 *
 * Endpoints:
 *   /api/auth/me               - user info
 *   /api/tasks?completed=1     - liczba wykonanych zadań sezonu
 *   /api/plots/all-with-details - liczba zagonów + grządek
 */

function userInitials(user) {
  if (!user) return '?';
  const src = user.public_display_name || user.username || user.email || '';
  return src.trim().slice(0, 2).toUpperCase();
}

function userLabel(user) {
  if (!user) return '';
  return user.public_display_name || user.username || user.email || '';
}

function seasonNumber(user) {
  if (!user?.created_at) return 1;
  const created = new Date(user.created_at);
  const now = new Date();
  return Math.max(1, now.getFullYear() - created.getFullYear() + 1);
}

// ── Mini stat (3-kolumnowa kafelka) ─────────────────────────────
function MiniStat({ lab, value, unit }) {
  return (
    <div style={{
      background: 'var(--t-bg-elev)',
      padding: '14px 10px',
      textAlign: 'center',
    }}>
      <Meta style={{ marginBottom: 4 }}>{lab}</Meta>
      <Display size={22}>{value}</Display>
      {unit && (
        <div style={{
          fontFamily: 'var(--t-mono)', fontSize: 10,
          letterSpacing: '.1em', color: 'var(--t-text-mute)',
          marginTop: 2, textTransform: 'uppercase',
        }}>{unit}</div>
      )}
    </div>
  );
}

// ── Pojedynczy wiersz menu ───────────────────────────────────────
function MenuRow({ label, sublabel, sublabelTone, to, onClick, danger }) {
  const inner = (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14,
      padding: '14px 0', cursor: 'pointer',
      transition: 'opacity 200ms ease',
    }}
    onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
    onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Display size={20} style={{
          color: danger ? 'var(--t-rust)' : 'var(--t-text)',
          letterSpacing: '-.01em', lineHeight: 1.1,
          display: 'block',
        }}>{label}</Display>
        {sublabel && (
          <div style={{
            fontSize: 12,
            color: sublabelTone === 'sage' ? 'var(--t-sage)' : 'var(--t-text-dim)',
            marginTop: 3,
            fontFamily: sublabelTone === 'sage' ? 'var(--t-mono)' : 'var(--t-sans)',
            letterSpacing: sublabelTone === 'sage' ? '.14em' : 0,
            textTransform: sublabelTone === 'sage' ? 'uppercase' : 'none',
          }}>{sublabel}</div>
        )}
      </div>
      <svg width="7" height="14" viewBox="0 0 7 14" fill="none" style={{ flexShrink: 0 }}>
        <path d="M1 1l5 6-5 6" stroke="var(--t-text-dim)" strokeWidth="1.6"
              strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
  if (to) return <Link to={to} style={{ textDecoration: 'none' }}>{inner}</Link>;
  return <div onClick={onClick}>{inner}</div>;
}

function MenuDivider() {
  return <div style={{ height: 1, background: 'var(--t-line)', margin: '8px 0' }} />;
}

// ── Sub-item wewnątrz huba (wcięta pozycja) ──────────────────────
function HubItem({ label, sublabel, to }) {
  return (
    <Link to={to} style={{
      textDecoration: 'none',
      display: 'block',
      padding: '10px 0 10px 16px',
      borderLeft: '2px solid var(--t-line)',
      marginLeft: 14,
      transition: 'background 200ms ease',
    }}
    onMouseEnter={e => e.currentTarget.style.background = 'var(--t-dim-bg)'}
    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      <div style={{
        fontSize: 14,
        color: 'var(--t-text)',
        letterSpacing: '-.01em',
      }}>{label}</div>
      {sublabel && (
        <div style={{
          fontSize: 11,
          color: 'var(--t-text-dim)',
          marginTop: 2,
          fontFamily: 'var(--t-mono)',
          letterSpacing: '.1em',
          textTransform: 'uppercase',
        }}>{sublabel}</div>
      )}
    </Link>
  );
}

// ── Główny komponent ────────────────────────────────────────────
export default function Menu() {
  const [theme, setThemeState] = useState(getTheme());
  const onToggleTheme = () => setThemeState(toggleTheme());
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [quickPhotoOpen, setQuickPhotoOpen] = useState(false);

  const { results, loading, errors } = useMultipleApi({
    me: '/api/auth/me',
    tasks: '/api/tasks?limit=500',
    plots: '/api/plots/all-with-details',
  });

  if (errors.me && (errors.me.includes('autoryz') || errors.me === 'Brak tokenu autoryzacji')) {
    return <NeedsLoginState />;
  }
  if (loading) return <LoadingState label={t('common.loading')} />;

  const user = results.me?.user;
  const allTasks = results.tasks?.data || [];
  const completedTasks = allTasks.filter(t => t.completed).length;
  const allPlots = results.plots || [];
  const allBeds = allPlots.flatMap(p => p.beds || []);

  // Suma plonu w kg z całego sezonu
  const totalYieldKg = allBeds
    .filter(b => b.actual_harvest_date && b.yield_amount && b.yield_unit?.toLowerCase().includes('kg'))
    .reduce((sum, b) => sum + (parseFloat(b.yield_amount) || 0), 0);

  return (
    <div style={{
      minHeight: 'var(--t-vh)',
      background: 'var(--t-bg)',
      paddingBottom: 60,
    }}>
      {/* Top bar - close + label */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '20px 20px 0',
        maxWidth: 720, margin: '0 auto',
      }}>
        <div
          onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/dashboard')}
          style={{
            width: 36, height: 36, borderRadius: 10,
            background: 'var(--t-bg-elev)', border: '1px solid var(--t-line)',
            display: 'grid', placeItems: 'center',
            fontFamily: 'var(--t-mono)', fontSize: 18,
            color: 'var(--t-text)', cursor: 'pointer',
          }}
          aria-label={t('common.close')}
        >×</div>
        <Meta>{t('nav.profile').toUpperCase()}</Meta>
        <div style={{ width: 36 }} />
      </div>

      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        {/* Identity card */}
        <div className="t-stagger" style={{ padding: '32px 24px 20px' }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 20 }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%',
              background: 'var(--t-sage)', color: 'var(--t-bg)',
              display: 'grid', placeItems: 'center',
              fontFamily: 'var(--t-display)', fontSize: 26, fontWeight: 400,
            }}>{userInitials(user)}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Display size={28} style={{
                display: 'block', letterSpacing: '-.02em', lineHeight: 1,
              }}>{userLabel(user)}</Display>
              <div style={{ fontSize: 12, color: 'var(--t-text-dim)', marginTop: 4 }}>
                {t('nav.season_label', { n: seasonNumber(user) })}
                {user?.city && ` · ${user.city}`}
                {user?.hardiness_zone && ` · strefa ${user.hardiness_zone}`}
              </div>
            </div>
          </div>

          {/* Season stats - 3 kolumny */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: 1, background: 'var(--t-line-2)',
            borderRadius: 12, overflow: 'hidden',
          }}>
            <MiniStat lab="Zadania" value={completedTasks} unit="wyk." />
            <MiniStat lab="Zbiory" value={totalYieldKg.toFixed(1)} unit="kg" />
            <MiniStat lab="Grządki" value={allBeds.length} unit="" />
          </div>
        </div>

        <Rule label="Szybkie akcje" />

        <div style={{ padding: '0 24px' }}>
          <MenuRow
            label="📷 Dodaj zdjęcie"
            sublabel="aparat lub galeria · auto tag + lokalizacja"
            sublabelTone="sage"
            onClick={() => setQuickPhotoOpen(true)}
          />
          <MenuRow
            label="Pełnoekranowy aparat"
            sublabel="live preview · 3 tryby (foto / rozpoznaj / choroba)"
            to="/gallery/capture"
          />
          <MenuRow
            label="Nowe zadanie"
            sublabel="dodaj zadanie do harmonogramu"
            to="/tasks/new"
          />
        </div>

        <Rule label="Twoje konto" />

        <div style={{ padding: '0 24px' }}>
          <MenuRow
            label="Mój profil"
            sublabel="awatar, imię, lokalizacja, strefa"
            to="/profile"
          />
          <MenuRow
            label="Mój ogród"
            sublabel="lokalizacja, gleba, nasłonecznienie"
            to="/profile/garden"
          />
          <MenuRow
            label="Profil publiczny"
            sublabel="udostępnij swój ogród innym"
            to="/profile/public"
          />
          <MenuRow
            label="Konto i bezpieczeństwo"
            sublabel="email, hasło, usuwanie konta"
            to="/profile/account"
          />
          <MenuRow
            label="Rok w ogrodzie"
            sublabel="podsumowanie sezonu — zbiory, zdjęcia, zadania"
            to="/rok"
          />
          <MenuRow
            label="Statystyki konta"
            sublabel="aktywność, zbiory, sezony"
            to="/profile/stats"
          />
        </div>

        <Rule label="Społeczność" />

        <div style={{ padding: '0 24px' }}>
          <MenuRow
            label="Aktywność znajomych"
            sublabel="zbiory i zdjęcia"
            to="/feed"
          />
          <MenuRow
            label="Giełda roślin"
            sublabel="sprzedaj, wymień, oddaj"
            to="/gielda"
          />
          <MenuRow
            label="Znajomi"
            sublabel="lista, zaproszenia, szukaj"
            to="/znajomi"
          />
        </div>

        <Rule label="Twój ogród" />

        <div style={{ padding: '0 24px' }}>
          <MenuRow label="Moja działka" sublabel={`${allPlots.length} ${allPlots.length === 1 ? 'działka' : allPlots.length < 5 ? 'działki' : 'działek'} · ${allBeds.length} grządek`} to="/plots" />
          <MenuRow label={t('nav.plants')} sublabel={`${allBeds.length} odmian w ogrodzie`} to="/plants" />
          <MenuRow label={t('nav.journal')} sublabel="notatki z sezonu" to="/journal" />

          {/* HUB: Pielęgnacja */}
          <div style={{ marginTop: 12 }}>
            <Meta style={{ marginBottom: 8 }}>Pielęgnacja</Meta>
            <HubItem label="Opryski" sublabel="historia, karencje" to="/care" />
            <HubItem label="Nawożenie" sublabel="harmonogram" to="/care/fertilization" />
          </div>

          {/* HUB: Harmonogram */}
          <div style={{ marginTop: 12 }}>
            <Meta style={{ marginBottom: 8 }}>Harmonogram</Meta>
            <HubItem label="Kwitnienie" sublabel="oś czasu" to="/garden-tasks" />
            <HubItem label="Sezonowe" sublabel="zima, propagacja" to="/garden-tasks/seasonal" />
            <HubItem label="Sukcesja" sublabel="ciągłość zbiorów" to="/garden-tasks/routine" />
          </div>

          <MenuRow label="Pogoda" sublabel="prognoza, alerty" to="/pogoda" />
          <MenuRow label="Choroby i szkodniki" sublabel="diagnoza, objawy" to="/problemy" />
          <MenuRow label="Zadania" sublabel="lista wszystkich" to="/tasks" />
          <MenuRow label="Co posadzić?" sublabel="podpowiedzi na miesiąc" to="/co-posadzic" />
          <MenuRow label="Kalendarz ogrodnika" sublabel="siej · sadź · zbieraj" to="/kalendarz-ogrodnika" />
          <MenuRow label="Planer sezonu" sublabel="rozpisz co posadzić" to="/garden-planner" />
          <MenuRow label="Magazyn nasion" sublabel="zapasy nasion · alerty ważności" to="/nasiona" />
          <MenuRow label="Moje odmiany" sublabel="oceny sprawdzone u mnie" to="/moje-odmiany" />
          <MenuRow label="Rozsada" sublabel="wysiew → pikowanie → hartowanie → wysadzenie" to="/rozsada" />
        </div>

        <Rule label="Galeria i media" />

        <div style={{ padding: '0 24px' }}>
          <MenuRow label="Galeria zdjęć" sublabel="wszystkie zdjęcia" to="/gallery" />
          <MenuRow label="Aparat" sublabel="zrób zdjęcie" to="/gallery/capture" />
          <MenuRow label="Przypomnienia" sublabel="karencje, zadania" to="/reminders" />
        </div>

        {/* Admin section — tylko dla admina */}
        {(user?.role === 'admin' || user?.username === 'admin') && (
          <>
            <Rule label="Administracja" />
            <div style={{ padding: '0 24px' }}>
              <MenuRow
                label="Panel administratora"
                sublabel="userzy, rośliny, zdjęcia, zdrowie systemu"
                to="/admin"
              />
              <MenuRow
                label="Zatwierdzanie roślin"
                sublabel="pending submissions"
                to="/admin/plants"
              />
              <MenuRow
                label="Moderacja zdjęć"
                sublabel="zgłoszone do galerii publicznej"
                to="/admin/photo-reviews"
              />
            </div>
          </>
        )}

        <Rule label="Narzędzia i ustawienia" />

        <div style={{ padding: '0 24px' }}>
          <MenuRow
            label={theme === 'light' ? 'Tryb ciemny' : 'Tryb jasny'}
            sublabel={theme === 'light' ? 'wróć do ciemnego motywu' : 'papierowy motyw — czytelny w słońcu'}
            onClick={onToggleTheme}
          />
          <MenuRow label="Statystyki" sublabel="plony, zadania" to="/analytics" />
          <MenuRow label="Eksport danych" sublabel="JSON · CSV · iCal" to="/export" />
        </div>

        <Rule label="Pomoc" />

        <div style={{ padding: '0 24px' }}>
          <MenuRow label="Pomoc i FAQ" sublabel="instrukcje, kontakt" to="/pomoc" />
          <MenuRow label="Polityka prywatności" sublabel="cookies, RODO" to="/privacy-policy" />
        </div>

        <div style={{
          padding: '0 24px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          gap: 14,
        }}>
          <Meta>{t('language.label')}</Meta>
          <LanguageSwitcher compact />
        </div>

        <MenuDivider />

        <div style={{ padding: '0 24px' }}>
          <MenuRow
            label="Wyloguj"
            danger
            onClick={async () => {
              try {
                const axios = (await import('../../config/axios')).default;
                await axios.post('/api/auth/logout');
              } catch {}
              window.location.href = '/login';
            }}
          />
        </div>

        {/* Editorial cytat */}
        <div style={{
          padding: '36px 24px 8px',
          marginTop: 20,
          textAlign: 'center',
        }}>
          <div style={{
            fontFamily: 'var(--t-display)',
            fontStyle: 'italic',
            fontSize: 14,
            color: 'var(--t-text-mute)',
            letterSpacing: '-.01em',
            lineHeight: 1.5,
            textWrap: 'balance',
          }}>
            „Najlepsza pora sadzenia drzewa była 20 lat temu.<br />
            Druga najlepsza — dziś.”
          </div>
        </div>
      </div>

      <QuickPhotoModal
        open={quickPhotoOpen}
        onClose={() => setQuickPhotoOpen(false)}
        onSuccess={() => setQuickPhotoOpen(false)}
      />
    </div>
  );
}
